import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Client } from '@twilio/conversations'

import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import type { CareHistoryProvider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'

type MessageCenterTarget = {
  clientId: string
  providerId: string
  threadId: string
  draftCounterpart?: CareHistoryProvider
  message: string
}

// The provider whose conversation the next client profile messenger opens to.
type ClientProfileMessengerTarget = {
  clientId: string
  counterpart: CareHistoryProvider
  // New for every click, so a repeat click reopens the provider even from another thread.
  requestId: string
}

type MessagesState = {
  activeClientMessengerId: string | null
  messageCenterTarget: MessageCenterTarget | null
  clientProfileMessengerTarget: ClientProfileMessengerTarget | null
  selectedConversationId: string | null
  conversationsClient?: Client
  inLiveMsgSession: boolean
  activeLiveMsgAppt?: Appointment
  liveMsgClientIdToOpen: string | null
  drafts: { content: string; conversationId: string }[]
  insertSmartSuggestionForClientId: string | null
  insertMessageTemplateForClientId: string | null
  insertEssentialsArticleForClientId: string | null
  essentialsArticleUrl: string | null
  pendingInitialThreadId: string | null
  resolvedConversations: ConversationObject[]
  // Text from a failed send that has no failed bubble to retry from (its conversation was just
  // created or replaced). Lives here so it survives the composer remounting for that conversation.
  unsentMessage: { conversationId: string; body: string } | null
}

const initialState: MessagesState = {
  activeClientMessengerId: null,
  messageCenterTarget: null,
  clientProfileMessengerTarget: null,
  selectedConversationId: null,
  drafts: [],
  inLiveMsgSession: false,
  liveMsgClientIdToOpen: null,
  insertSmartSuggestionForClientId: null,
  insertMessageTemplateForClientId: null,
  insertEssentialsArticleForClientId: null,
  essentialsArticleUrl: null,
  pendingInitialThreadId: null,
  resolvedConversations: [],
  unsentMessage: null,
}

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setActiveClientMessengerId: (state, action: PayloadAction<string | null>) => {
      state.activeClientMessengerId = action.payload
    },
    setMessageCenterTarget: (state, action: PayloadAction<MessageCenterTarget | null>) => {
      state.messageCenterTarget = action.payload
    },
    setClientProfileMessengerTarget: (state, action: PayloadAction<ClientProfileMessengerTarget | null>) => {
      state.clientProfileMessengerTarget = action.payload
    },
    setConversationsClient: (state, action: PayloadAction<Client>) => {
      // @ts-ignore
      state.conversationsClient = action.payload
    },
    setSelectedConversation: (state, action: PayloadAction<string>) => {
      state.selectedConversationId = action.payload
    },
    toggleLiveMsgSession: (state, action: PayloadAction<boolean>) => {
      state.inLiveMsgSession = action.payload
    },
    setActiveLiveMsgAppt: (state, action: PayloadAction<Appointment | null>) => {
      state.activeLiveMsgAppt = action.payload ?? undefined
    },
    setLiveMsgClientIdToOpen: (state, action: PayloadAction<string | null>) => {
      state.liveMsgClientIdToOpen = action.payload
    },
    saveMessageDraft: (state, action: PayloadAction<{ content: string; conversationId: string }>) => {
      const draftIdx = state.drafts.findIndex((draft) => draft.conversationId === action.payload.conversationId)
      if (draftIdx !== -1) {
        state.drafts[draftIdx] = action.payload
      } else {
        state.drafts.push(action.payload)
      }
    },
    clearSelectedConversation: (state) => {
      state.selectedConversationId = null
    },
    clearLiveMsgSession: (state) => {
      state.inLiveMsgSession = false
      state.activeLiveMsgAppt = undefined
      state.liveMsgClientIdToOpen = null
    },
    clearLiveMsgClientIdToOpen: (state) => {
      state.liveMsgClientIdToOpen = null
    },
    clearMessages: () => {
      return initialState
    },
    updateSelectedConversationID: (
      state,
      action: PayloadAction<{ oldConversationId: string; newConversationId: string }>,
    ) => {
      if (state.selectedConversationId === action.payload.oldConversationId) {
        state.selectedConversationId = action.payload.newConversationId
      }
    },
    setInsertSmartSuggestionForClientId: (state, action: PayloadAction<string | null>) => {
      state.insertSmartSuggestionForClientId = action.payload
    },
    setInsertMessageTemplateForClientId: (state, action: PayloadAction<string | null>) => {
      state.insertMessageTemplateForClientId = action.payload
    },
    setInsertEssentialsArticleForClientId: (state, action: PayloadAction<string | null>) => {
      state.insertEssentialsArticleForClientId = action.payload
    },
    setEssentialsArticleUrl: (state, action: PayloadAction<string | null>) => {
      state.essentialsArticleUrl = action.payload
    },
    setPendingInitialThreadId: (state, action: PayloadAction<string | null>) => {
      state.pendingInitialThreadId = action.payload
    },
    setUnsentMessage: (state, action: PayloadAction<{ conversationId: string; body: string } | null>) => {
      state.unsentMessage = action.payload
    },
    upsertResolvedConversation: (state, action: PayloadAction<ConversationObject>) => {
      const conversationIndex = state.resolvedConversations.findIndex(
        (conversation) => conversation.conversation_id === action.payload.conversation_id,
      )
      if (conversationIndex === -1) {
        state.resolvedConversations.push(action.payload)
      } else {
        state.resolvedConversations[conversationIndex] = action.payload
      }
    },
  },
})

export const {
  setActiveClientMessengerId,
  setMessageCenterTarget,
  setClientProfileMessengerTarget,
  setConversationsClient,
  setSelectedConversation,
  clearSelectedConversation,
  clearMessages,
  clearLiveMsgSession,
  clearLiveMsgClientIdToOpen,
  saveMessageDraft,
  toggleLiveMsgSession,
  setActiveLiveMsgAppt,
  setLiveMsgClientIdToOpen,
  updateSelectedConversationID,
  setInsertSmartSuggestionForClientId,
  setInsertMessageTemplateForClientId,
  setInsertEssentialsArticleForClientId,
  setEssentialsArticleUrl,
  setPendingInitialThreadId,
  setUnsentMessage,
  upsertResolvedConversation,
} = messagesSlice.actions
export default messagesSlice.reducer
